import { useRef, useState, useCallback, useEffect } from 'react';

interface UseSwipeOptions {
  threshold?: number;
  totalItems: number;
}

export function useSwipe({ threshold = 50, totalItems }: UseSwipeOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const goTo = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, totalItems - 1));
    setCurrentIndex(clamped);
    if (containerRef.current) {
      const cardWidth = containerRef.current.offsetWidth;
      containerRef.current.scrollTo({ left: clamped * cardWidth, behavior: 'smooth' });
    }
  }, [totalItems]);

  const goNext = useCallback(() => goTo(currentIndex + 1), [goTo, currentIndex]);
  const goPrev = useCallback(() => goTo(currentIndex - 1), [goTo, currentIndex]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e: TouchEvent) => {
      touchEndX.current = e.changedTouches[0].clientX;
      const diff = touchStartX.current - touchEndX.current;
      if (Math.abs(diff) > threshold) {
        if (diff > 0) goNext();
        else goPrev();
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [threshold, goNext, goPrev]);

  // Sync index with scroll position (for CSS scroll-snap)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    let scrollTimeout: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const cardWidth = container.offsetWidth;
        if (cardWidth > 0) {
          const newIndex = Math.round(container.scrollLeft / cardWidth);
          setCurrentIndex(Math.max(0, Math.min(newIndex, totalItems - 1)));
        }
      }, 100);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [totalItems]);

  return { containerRef, currentIndex, goTo, goNext, goPrev };
}
